import React from 'react';
import { log } from '../utils/logger';

export type Actions<T> =
    | { type: 'RECEIVE_ITEMS'; items: T[] }
    | { type: 'START_FETCH_ITEMS' }
    | { type: 'FETCH_ERROR'; error: string };

interface IState<T> {
    items: T[];
    offset: number;
    isLoading: boolean;
    shouldShowButton: boolean;
    totalCount: number;
}

export const reducer = <T>(state: IState<T>, action: Actions<T>) => {
    switch (action.type) {
        case 'START_FETCH_ITEMS':
            return {
                ...state,
                isLoading: true,
            };
        case 'RECEIVE_ITEMS':
            const items = [...state.items, ...action.items];
            return {
                ...state,
                items,
                offset: state.offset + action.items.length,
                shouldShowButton: items.length < state.totalCount,
                isLoading: false,
            };
        case 'FETCH_ERROR':
            log.error('Fetching failed at load-more reducer', action.error);
            return {
                ...state,
                isLoading: false,
            };
    }
    return state;
};

type ILoadMoreResponse<T> = Pick<IState<T>, 'items' | 'shouldShowButton' | 'isLoading'> & {
    getMoreItems: () => void;
};
export const useLoadMore = <T>(
    cb: (offset: number, limit: number) => Promise<T[]>,
    itemsToFetch: number,
    initialItems: T[],
    totalCount: number,
): ILoadMoreResponse<T> => {
    const initialState: IState<T> = {
        items: initialItems,
        offset: initialItems.length,
        isLoading: false,
        shouldShowButton: initialItems.length < totalCount,
        totalCount,
    };

    const [{ items, offset, shouldShowButton, isLoading }, dispatch] = React.useReducer<
        (state: IState<T>, action: Actions<T>) => IState<T>
    >(reducer, initialState);

    const getData = () => {
        dispatch({ type: 'START_FETCH_ITEMS' });
        cb(offset, itemsToFetch)
            .then((items: T[]) => {
                dispatch({ type: 'RECEIVE_ITEMS', items });
            })
            .catch(error => {
                dispatch({ type: 'FETCH_ERROR', error });
            });
    };

    return {
        getMoreItems: getData,
        items,
        shouldShowButton,
        isLoading,
    };
};
